import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Dropdown, Overlay } from 'react-bootstrap';
import { faEllipsis } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './contextMenu.styles.css';

/**
 * Custom dropdown toggle without the default arrow
 */
const CustomToggle = React.forwardRef(({ children, onClick, className, disabled }, ref) => (
  <button
    ref={ref}
    type="button"
    className={`context-menu-toggle btn btn-link text-secondary p-8 rounded-circle ${className || ''}`}
    onClick={onClick}
    disabled={disabled}
    style={{
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
    onMouseOver={(e) => { e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.05)'; }}
    onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
    onFocus={(e) => { e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.05)'; }}
    onBlur={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
    aria-label="Menu options"
  >
    {children}
  </button>
));

/**
 * ContextMenu - A reusable dropdown menu component
 *
 * @param {Object} props - Component props
 * @param {Array} props.menuItems - Array of menu items with label, onClick, and optional divider and variant
 * @param {string} props.toggleIcon - Optional custom icon component, defaults to three dots
 * @param {string} props.align - Alignment of the dropdown menu ('start', 'end')
 * @param {string} props.className - Additional CSS classes for the toggle button
 * @param {string} props.id - Optional ID for the dropdown
 * @param {boolean} props.disabled - Whether the dropdown is disabled
 * @returns {JSX.Element} - Rendered component
 */
const ContextMenu = ({
  menuItems,
  toggleIcon,
  align = 'end',
  className,
  id,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  // Unique ID for this dropdown instance
  const idRef = useRef(`dropdown-${Math.random().toString(36).slice(2, 9)}`);
  const toggleRef = useRef(null);

  /**
   * Handles global dropdown toggle events
   * Closes this dropdown if another dropdown is opened
   */
  const handleGlobalDropdown = useCallback((e) => {
    if (e.detail !== idRef.current) {
      setIsOpen(false);
    }
  }, []);

  const handleClose = useCallback((e) => {
    const menu = document.getElementById('dropdown-menu');
    if (menu && !menu.contains(e.target)) {
      setIsOpen(false);
    }
  }, [setIsOpen]);

  // Set up and clean up global event listener
  useEffect(() => {
    document.addEventListener('dropdown-toggle', handleGlobalDropdown);
    document.addEventListener('click', handleClose);
    return () => {
      document.removeEventListener('dropdown-toggle', handleGlobalDropdown);
      document.removeEventListener('click', handleClose);
    };
  }, [handleGlobalDropdown, handleClose]);

  /**
   * Handles click events on the dropdown toggle
   * Manages the global currentlyOpenId state
   */
  const handleClick = useCallback((e) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
    if (!isOpen) {
      // Dispatch event to notify other dropdowns
      document.dispatchEvent(new CustomEvent('dropdown-toggle', { detail: idRef.current }));
    }
  }, [isOpen, idRef]);

  /**
   * Handles click events on dropdown menu items
   * Closes dropdown and calls the item's onClick handler
   */
  const handleItemClick = useCallback((item, e) => {
    e.stopPropagation();
    setIsOpen(false);
    item.onClick(e);
  }, []);

  // Default icon is three vertical dots
  const icon = toggleIcon || <FontAwesomeIcon icon={faEllipsis} style={{ color: 'white' }} />;

  // Wrap the onClick handlers to stop propagation
  const wrappedMenuItems = menuItems.map(item => {
    if (item.divider) return item;

    return {
      ...item,
      onClick: (e) => handleItemClick(item, e),
    };
  });

  // Custom render function to prevent passing extra props to DOM elements
  const renderMenu = (menuProps) => {
    // Extract only the props we need for the Dropdown.Menu
    const { show, style, className: menuClassName, children } = menuProps;

    return (
      <div style={style} className={menuClassName}>
        <Dropdown.Menu id="dropdown-menu" role="menu" aria-label="Dropdown menu" show={show}>
          {children}
        </Dropdown.Menu>
      </div>
    );
  };

  return (
    <Dropdown align={align} className="context-menu-dropdown" show={isOpen} onToggle={() => { }}>
      <Dropdown.Toggle
        as={CustomToggle}
        className={className}
        disabled={disabled}
        id={id || 'dropdown-context-menu'}
        onClick={handleClick}
        ref={toggleRef}
      >
        {icon}
      </Dropdown.Toggle>

      <Overlay
        target={toggleRef.current}
        show={isOpen}
        placement="bottom-end"
      >
        {({ placement, arrowProps, show, popper, ...props }) => renderMenu({
          show,
          ...props,
          children: wrappedMenuItems.map((item, index) => {
            // If it's a divider, render a divider
            if (item.divider) {
              return <Dropdown.Divider key={`divider-${index}`} />;
            }

            // Otherwise render a dropdown item
            return (
              <Dropdown.Item
                key={`item-${index}`}
                onClick={item.onClick}
                className={`${item.variant ? `text-${item.variant}` : ''}`}
                disabled={item.disabled}
              >
                {item.icon && <span className="me-16">{item.icon}</span>}
                {item.label}
              </Dropdown.Item>
            );
          }),
        })}
      </Overlay>
    </Dropdown>
  );
};

export default ContextMenu;
